// The bell in the header: mentions, thread replies, direct messages and tasks, newest first.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { NOTIFICATION_LABEL as LABEL, formatShort, keys, notificationHref, plainText, type Notification } from "../../lib/chat";
import { Icon } from "../../components/icons";
import { Avatar, cx } from "../../components/ui";


export function NotificationsBell({ slug }: { slug: string }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const data = useQuery({ queryKey: keys.notifications(slug), queryFn: () => api<{ unread: number; notifications: Notification[] }>(`/orgs/${slug}/notifications`) });
  const unread = data.data?.unread ?? 0;

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);

  async function markRead(ids?: string[]) {
    await api(`/orgs/${slug}/notifications/read`, { method: "POST", body: ids ? { ids } : {} }).catch(() => undefined);
    qc.invalidateQueries({ queryKey: keys.notifications(slug) });
  }

  function openOne(n: Notification) {
    setOpen(false);
    if (!n.read) void markRead([n.id]);
    navigate(notificationHref(slug, n));
  }

  return (
    <div className="relative" ref={box}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"} aria-expanded={open} aria-haspopup="dialog"
        className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
        <Icon name="bell" />
        {unread > 0 && <span className="absolute right-0.5 top-0.5 min-w-4 rounded-full bg-red-600 px-1 text-center text-[10px] font-semibold leading-4 text-white">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div role="dialog" aria-label="Notifications" className="absolute right-0 z-30 mt-2 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <h2 className="font-semibold">Notifications</h2>
            {unread > 0 && <button type="button" onClick={() => markRead()} className="text-sm text-brand-600 hover:underline">Mark all as read</button>}
          </div>
          <ul className="max-h-[28rem] overflow-y-auto">
            {data.data?.notifications.length ? data.data.notifications.map((n) => (
              <li key={n.id}>
                <button type="button" onClick={() => openOne(n)} className={cx("flex w-full gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800", !n.read && "bg-brand-50/60 dark:bg-brand-700/10")}>
                  <Avatar name={n.actor?.name ?? "?"} userId={n.actor?.id} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm"><span className="font-medium">{n.actor?.name ?? "Someone"}</span> {LABEL[n.kind]}</span>
                    {n.preview && <span className="mt-0.5 block truncate text-sm text-slate-500">{plainText(n.preview, new Map())}</span>}
                    <span className="mt-0.5 block text-xs text-slate-400">{formatShort(n.createdAt)}</span>
                  </span>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-600" aria-label="Unread" />}
                </button>
              </li>
            )) : <li className="px-4 py-8 text-center text-sm text-slate-500">Nothing new. Mentions, replies, direct messages and tasks given to you show up here.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
